import { useEffect, useMemo, useState } from "react";
import { Alert, AutoComplete, Button, InputNumber, Modal, Select, message } from "antd";
import { ClipboardPen, Plus, X } from "lucide-react";
import { mixGetV1 } from "../../services/mixin";
import { emptyLine, lineMissing, useExecLog, type ExecLine } from "../AppTuVan/execLog";
import type { Visit } from "../AppTuVan/logic";
import { useReception } from "../AppTuVan/receptionApi";
import { KT_MACHINES } from "../KeToan/ktCards";
import { ktApi } from "../KeToan/ktStore";

/** Toàn bộ nhân sự cho ô Phụ mổ, Điều dưỡng: danh sách Lễ tân chỉ có tư vấn viên, bác sĩ, KTV */
let staffLoad: Promise<string[]> | undefined;
const loadStaffNames = () => {
  staffLoad ??= mixGetV1("/api/v1/be/staffs", { type_user: 1, limit: 200 })
    .then((res: { data?: { items?: { name?: string }[] } }) => (res?.data?.items ?? []).map((u) => u.name ?? "").filter(Boolean))
    .catch(() => { staffLoad = undefined; return [] as string[]; });
  return staffLoad;
};

const ROLES: { key: "doctor" | "assistant" | "nurse" | "ktv"; label: string }[] = [
  { key: "doctor", label: "Bác sĩ chính" }, { key: "assistant", label: "Phụ mổ" }, { key: "nurse", label: "Điều dưỡng" }, { key: "ktv", label: "KTV" },
];

/** Lễ tân ghi ai làm, máy nào theo từng dịch vụ: Kế toán lấy số này để tính tua, khấu hao */
export default function ExecLogPanel({ v }: { v: Visit }) {
  const record = useExecLog((s) => s.records[v.id]);
  const syncVisit = useExecLog((s) => s.syncVisit);
  const saveLines = useExecLog((s) => s.saveLines);
  const staffs = useReception((s) => s.staffs);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<ExecLine[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<string[]>([]);

  // staffs đổi là lúc danh sách tên vừa tải xong: điền lại người còn trống
  useEffect(() => { syncVisit(v); }, [v, staffs, syncVisit]);

  const showModal = () => {
    setLines(record?.lines ?? []);
    setCatalog(Array.from(new Set(ktApi.snapshot().services.filter((x) => x.type !== "product").map((x) => x.name))));
    loadStaffNames().then(setNames);
    setOpen(true);
  };
  const roleOptions = (role: "doctor" | "assistant" | "nurse" | "ktv") => {
    const own = staffs.filter((x) => (role === "doctor" ? x.reception_role === "doctor" : role === "ktv" ? x.reception_role === "technician" : false)).map((x) => x.name);
    return Array.from(new Set([...own, ...(role === "assistant" || role === "nurse" ? names : [])])).map((n) => ({ value: n, label: n }));
  };
  const machines = useMemo(() => KT_MACHINES.map((m) => ({ value: m.code, label: `${m.code} · ${m.name}` })), []);
  const setLine = (id: string, p: Partial<ExecLine>) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...p } : l)));
  const missing = lines.some(lineMissing);
  const save = () => {
    if (lines.some((l) => !l.service.trim() && lines.length > 1)) { message.error("Nhập tên dịch vụ cho từng dòng"); return; }
    if (missing) { message.error("Mỗi dịch vụ cần ít nhất Bác sĩ, KTV hoặc Điều dưỡng"); return; }
    saveLines(v.id, lines);
    message.success("Đã lưu ekip, mã máy");
    setOpen(false);
  };

  if (!v.exec || !record) return null;
  const noMachine = record.lines.filter((l) => !l.machine).length;
  return (
    <>
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
        <span>{record.edited ? `Đã ghi ekip ${record.lines.length} dịch vụ${noMachine ? `, ${noMachine} dịch vụ chưa chọn máy` : ""}` : "Chưa ghi đủ ekip, mã máy để Kế toán tính tua"}</span>
        <Button size="small" icon={<ClipboardPen size={13} />} onClick={showModal}>{record.edited ? "Sửa" : "Ghi ekip, máy"}</Button>
      </div>
      <Modal open={open} width={760} title={`Ekip, mã máy · ${v.cust.name}`} okText="Lưu" cancelText="Thôi" onCancel={() => setOpen(false)} onOk={save} destroyOnHidden>
        <div className="space-y-3">
          <Alert type="info" showIcon message="Đang lưu tạm trên trình duyệt này. Kế toán cần dùng cùng máy mới thấy." />
          {lines.map((l, i) => (
            <div key={l.id} className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-end gap-2">
                <label className="block min-w-0 flex-1 text-sm">
                  <span className="text-slate-500">Dịch vụ</span>
                  <AutoComplete aria-label={`Dịch vụ ${i + 1}`} className="mt-1 w-full" value={l.service} onChange={(service) => setLine(l.id, { service })} placeholder="Chọn hoặc nhập tên dịch vụ"
                    options={catalog.map((n) => ({ value: n }))} filterOption={(q, o) => String(o?.value ?? "").toLowerCase().includes(q.toLowerCase())} />
                </label>
                {lines.length > 1 && <Button type="text" danger icon={<X size={14} />} aria-label={`Bỏ dòng ${i + 1}`} onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))} />}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {ROLES.map((r) => (
                  <label key={r.key} className="block text-sm">
                    <span className="text-slate-500">{r.label}</span>
                    <Select aria-label={`${r.label} dòng ${i + 1}`} className="mt-1 w-full" allowClear showSearch optionFilterProp="label" placeholder="Chọn người" value={l[r.key] || undefined} onChange={(val) => setLine(l.id, { [r.key]: val || "" })} options={roleOptions(r.key)} />
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-[1fr_120px] gap-2">
                <label className="block text-sm">
                  <span className="text-slate-500">Mã máy</span>
                  <Select aria-label={`Mã máy dòng ${i + 1}`} className="mt-1 w-full" allowClear placeholder="Không dùng máy thì bỏ trống" value={l.machine || undefined} onChange={(val) => setLine(l.id, { machine: val || "" })} options={machines} />
                </label>
                <label className="block text-sm">
                  <span className="text-slate-500">Số shot</span>
                  <InputNumber aria-label={`Số shot dòng ${i + 1}`} className="mt-1 !w-full" min={0} disabled={!l.machine} value={l.shots} onChange={(val) => setLine(l.id, { shots: val === null ? undefined : Number(val) })} />
                </label>
              </div>
            </div>
          ))}
          <Button size="small" icon={<Plus size={13} />} onClick={() => setLines((ls) => [...ls, emptyLine("", ls[0]?.doctor ?? "", ls[0]?.ktv ?? "")])}>Thêm dịch vụ</Button>
        </div>
      </Modal>
    </>
  );
}
